import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../common/spinner';
import { listPortfolios, savePortfolio, deletePortfolio } from '../modules/portfoliodata';
import { getTenants } from '../modules/tenantdata';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Portfolio } from '../domain/portfolio';
import Dialog from '@mui/material/Dialog';
import { DialogActions, DialogContent } from '@mui/material';
import { toast } from 'react-toastify';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';

const _ = require('lodash');

const styles = {
    formControl: { m: 1, minWidth: 120, maxWidth: 300 },
    select: { mt: 2, mx: 1, width: '95%' },
    textField: { mx: 1, width: '95%' },
    chips: { display: 'flex', flexWrap: 'wrap' },
    chip: { margin: '2px' },
};

export default function PortfolioEditView(props) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const portfolioId =
        props.match.params.portfolioId === 'new'
            ? props.match.params.portfolioId
            : parseInt(props.match.params.portfolioId, 10);

    const portfolios = useSelector((state) => state.portfoliodata.portfolios);
    const isFetchingPortfolios = useSelector((state) => state.portfoliodata.isFetching);
    const tenants = useSelector((state) => state.tenantdata.tenants);

    const [saving, setSaving] = useState(false);
    const [createOnProgress, setCreateOnProgress] = useState(false);
    const [portfolio, setPortfolio] = useState(null);
    const [formOk, setFormOk] = useState(false);
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

    useEffect(() => {
        if (portfolioId === 'new' && !portfolio) {
            setCreateOnProgress(true);
            Portfolio.NewObject('').then(function (pf) {
                setPortfolio(pf);
                setCreateOnProgress(false);
            });
        }
    }, [portfolioId, portfolio]);

    useEffect(() => {
        if (!tenants || tenants.length === 0) {
            dispatch(getTenants());
        }
    }, [tenants, dispatch]);

    useEffect(() => {
        //TODO: portfolio fetch failed case
        if (isFetchingPortfolios === false && (portfolios == null || portfolios.length === 0)) {
            dispatch(listPortfolios());
        } else if (portfolioId !== 'new' && !isFetchingPortfolios) {
            let portfolioToEdit = _.find(portfolios, ['id', portfolioId]);
            setPortfolio(portfolioToEdit);
            setFormOk(true);
        }
    }, [portfolios, dispatch, isFetchingPortfolios, portfolioId]);

    const checkIfFormOk = (newPortfolio) => {
        let ok = true;

        if (newPortfolio.name == null || newPortfolio.name.length === 0) ok = false;
        if (portfolioId === 'new' && newPortfolio.tenantCompanyClusterId == null) ok = false;
        if (ok !== formOk) {
            setFormOk(ok);
        }
    };

    const handleChange = (event) => {
        let newPortfolio = new Portfolio(portfolio);
        const target = event.target;
        const value = target.value;
        const name = target.name;
        switch (name) {
            case 'portfolioName':
                newPortfolio.name = value;
                break;
            case 'portfolioTenant':
                newPortfolio.tenantCompanyClusterId = value;
                break;
            default:
                break;
        }

        setPortfolio(newPortfolio);
        checkIfFormOk(newPortfolio);
    };

    const onCancel = () => {
        props.history.goBack();
    };

    const onSubmit = () => {
        let toBeSavePortfolio = new Portfolio(portfolio);
        toast.info('Tallennetaan..', { autoClose: 1000, hideProgressBar: true });
        setSaving(true);
        dispatch(savePortfolio(toBeSavePortfolio))
            .then(() => {
                if (portfolioId === 'new') {
                    props.history.replace('/admin/portfolio/edit/' + portfolio.id);
                }
                toast.info('Salkun tiedot tallennettu.', { autoClose: 1500, hideProgressBar: true });
                setSaving(false);
            })
            .catch(() => {
                toast.error('Salkun tallennus epäonnistui!', { autoClose: 5000, hideProgressBar: false });
                setSaving(false);
            });
    };

    const onRemove = () => {
        setConfirmDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        let toBeRemovedPortfolio = new Portfolio(portfolio);
        setSaving(true);
        toast.info('Poistetaan..', { autoClose: 1500, hideProgressBar: true });
        dispatch(deletePortfolio(toBeRemovedPortfolio.storageId))
            .then(() => {
                toast.info('Salkun tiedot poistettu.', { autoClose: 2000, hideProgressBar: true });
                //TODO: won't work if deleted immediately after creating new portfolio
                props.history.go(-2);
            })
            .catch(() => {
                toast.error('Salkun poisto epäonnistui!', { autoClose: 5000, hideProgressBar: false });
            });
    };

    const cancelDelete = () => {
        setConfirmDeleteModalOpen(false);
    };

    const ConfirmationDialog = () => {
        const theme = useTheme();
        const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
        if (confirmDeleteModalOpen)
            return (
                <Dialog
                    onClose={cancelDelete}
                    aria-labelledby="simple-dialog-title"
                    open={confirmDeleteModalOpen}
                    fullWidth={true}
                    maxWidth={'md'}
                    fullScreen={fullScreen}>
                    <DialogContent dividers>Oletko varma, että haluat poistaa salkun tiedot?</DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            color="primary"
                            name="btn-modal-poista"
                            id="modal-poista"
                            onClick={confirmDelete}
                            disabled={saving}>
                            Poista
                        </Button>
                        &nbsp;&nbsp;
                        <Button
                            variant="contained"
                            sx={{ bgcolor: 'grey.300', color: 'text.primary', '&:hover': { bgcolor: 'grey.400' } }}
                            name="btn-modal-peruuta"
                            id="modal-peruuta"
                            onClick={cancelDelete}
                            disabled={saving}>
                            Peruuta
                        </Button>
                    </DialogActions>
                </Dialog>
            );
        return null;
    };

    const getHeader = () => {
        if (portfolio == null) return null;
        return (
            <h2>
                <Button onClick={onCancel}>
                    <i className="fas fa-chevron-left fa-2x"></i>&nbsp;&nbsp;
                </Button>
                {portfolio.name}
            </h2>
        );
    };

    const renderView = () => {
        if (createOnProgress) {
            return <Spinner padding="20px" title="Alustetaan salkun tietoja" />;
        }
        if (isFetchingPortfolios) {
            return <Spinner padding="20px" title="Haetaan salkun tietoja" />;
        } else if (!portfolio) {
            //TODO: proper fail case handling, no data failure information is currently available
            return (
                <div style={{ padding: '20px' }}>
                    <Alert severity="error">Salkku haku epäonnistui. Kokeile ladata sivu uudestaan (F5).</Alert>
                </div>
            );
        }

        return (
            <fieldset disabled={saving}>
                <Grid container>
                    <Grid item xs={12}>
                        {getHeader()}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={portfolio.name == null || portfolio.name.length === 0}
                            id="portfolioName"
                            name="portfolioName"
                            label="Nimi"
                            value={portfolio.name}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl sx={styles.formControl} required error={portfolioId === 'new' && portfolio.tenantCompanyClusterId == null}>
                            <InputLabel id="portfolioTenantLabel">{t('general.tenant')}</InputLabel>
                            <Select
                                labelId="portfolioTenantLabel"
                                id="portfolioTenant"
                                name="portfolioTenant"
                                disabled={portfolioId !== 'new'}
                                value={portfolio.tenantCompanyClusterId || ''}
                                onChange={handleChange}>
                                {(tenants || []).map((tc) => (
                                    <MenuItem key={tc.clusterId} value={tc.clusterId}>
                                        {tc.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div style={styles.formControl}>
                            <InputLabel shrink id="portfolioUsersLabel">
                                {t('admin.users')}
                            </InputLabel>
                            <div style={styles.chips}>
                                {portfolio.users.length === 0 && <em>{t('admin.noUsersAssigned')}</em>}
                                {portfolio.users.map((puser) => (
                                    <Chip key={puser.user.id} label={puser.user.name} style={styles.chip} />
                                ))}
                            </div>
                            <em style={{ fontSize: 12 }}>{t('admin.editUsersFromUserView')}</em>
                        </div>
                    </Grid>

                    <Footer>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="secondary"
                                name="delete-portfolio"
                                onClick={onRemove}
                                disabled={portfolioId === 'new'}>
                                <i className="fas fa-trash"></i> &nbsp; Poista
                            </Button>
                        </Grid>

                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                name="save-portfolio"
                                onClick={onSubmit}
                                disabled={saving || !formOk}>
                                {saving && <i className="fas fa-spinner fa-spin"></i>}
                                {!saving && <i className="fas fa-save"></i>}
                                &nbsp;Tallenna
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                name="close"
                                onClick={onCancel}
                                disabled={saving}>
                                <i className="far fa-window-close"></i>&nbsp;Sulje
                            </Button>
                        </Grid>
                    </Footer>
                    <FooterPadding />

                    <ConfirmationDialog />
                </Grid>
            </fieldset>
        );
    };

    return renderView();
}
